import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const BatchInfoForm = ({ onSubmit, initialData = null, isEditing = false }) => {
  const [formData, setFormData] = useState({
    batchNumber: '',
    productName: '',
    productType: '',
    manufacturingDate: '',
    expiryDate: '',
    batchSize: '',
    lotNumber: '',
    manufacturingSite: '',
    storageConditions: '',
    packagingType: '',
    qualityGrade: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const productTypeOptions = [
    { value: 'tablet', label: 'Tablet' },
    { value: 'capsule', label: 'Capsule' },
    { value: 'liquid', label: 'Liquid/Syrup' },
    { value: 'injection', label: 'Injection' },
    { value: 'cream', label: 'Cream/Ointment' },
    { value: 'powder', label: 'Powder' },
    { value: 'suspension', label: 'Suspension' }
  ];

  const manufacturingSiteOptions = [
    { value: 'site_a', label: 'Manufacturing Site A - Athens' },
    { value: 'site_b', label: 'Manufacturing Site B - Thessaloniki' },
    { value: 'site_c', label: 'Manufacturing Site C - Patras' },
    { value: 'external', label: 'External Partner Facility' }
  ];

  const storageConditionOptions = [
    { value: 'room_temp', label: 'Room Temperature (15-25°C)' },
    { value: 'cold', label: 'Cold Storage (2-8°C)' },
    { value: 'frozen', label: 'Frozen (-20°C)' },
    { value: 'controlled', label: 'Controlled Room Temperature' },
    { value: 'dry', label: 'Dry Storage (<60% RH)' }
  ];

  const qualityGradeOptions = [
    { value: 'pharmaceutical', label: 'Pharmaceutical Grade' },
    { value: 'food', label: 'Food Grade' },
    { value: 'cosmetic', label: 'Cosmetic Grade' },
    { value: 'research', label: 'Research Grade' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.batchNumber?.trim()) {
      newErrors.batchNumber = 'Batch number is required';
    }

    if (!formData?.productName?.trim()) {
      newErrors.productName = 'Product name is required';
    }

    if (!formData?.productType) {
      newErrors.productType = 'Product type is required';
    }

    if (!formData?.manufacturingDate) {
      newErrors.manufacturingDate = 'Manufacturing date is required';
    }

    if (!formData?.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    }

    if (!formData?.batchSize || formData?.batchSize <= 0) {
      newErrors.batchSize = 'Valid batch size is required';
    }

    if (!formData?.manufacturingSite) {
      newErrors.manufacturingSite = 'Manufacturing site is required';
    }

    // Validate expiry date is after manufacturing date
    if (formData?.manufacturingDate && formData?.expiryDate) {
      const mfgDate = new Date(formData.manufacturingDate);
      const expDate = new Date(formData.expiryDate);
      if (expDate <= mfgDate) {
        newErrors.expiryDate = 'Expiry date must be after manufacturing date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      batchNumber: '',
      productName: '',
      productType: '',
      manufacturingDate: '',
      expiryDate: '',
      batchSize: '',
      lotNumber: '',
      manufacturingSite: '',
      storageConditions: '',
      packagingType: '',
      qualityGrade: '',
      notes: ''
    });
    setErrors({});
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Package" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {isEditing ? 'Edit Batch Information' : 'New Batch Information'}
            </h3>
            <p className="text-sm text-muted-foreground">
              Enter comprehensive batch details for pharmaceutical tracking
            </p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Batch Number"
            type="text"
            placeholder="e.g., BT-2024-001247"
            value={formData?.batchNumber}
            onChange={(e) => handleInputChange('batchNumber', e?.target?.value)}
            error={errors?.batchNumber}
            required
            className="font-mono"
          />

          <Input
            label="Lot Number"
            type="text"
            placeholder="e.g., LOT-24-Q3-0847"
            value={formData?.lotNumber}
            onChange={(e) => handleInputChange('lotNumber', e?.target?.value)}
            error={errors?.lotNumber}
            className="font-mono"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Product Name"
            type="text"
            placeholder="e.g., Paracetamol 500mg"
            value={formData?.productName}
            onChange={(e) => handleInputChange('productName', e?.target?.value)}
            error={errors?.productName}
            required
          />

          <Select
            label="Product Type"
            placeholder="Select product type"
            options={productTypeOptions}
            value={formData?.productType}
            onChange={(value) => handleInputChange('productType', value)}
            error={errors?.productType}
            required
          />
        </div>

        {/* Manufacturing Details */}
        <div className="border-t border-border pt-6">
          <h4 className="text-md font-medium text-foreground mb-4">Manufacturing Details</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Manufacturing Date"
              type="date"
              value={formData?.manufacturingDate}
              onChange={(e) => handleInputChange('manufacturingDate', e?.target?.value)}
              error={errors?.manufacturingDate}
              required
            />

            <Input
              label="Expiry Date"
              type="date"
              value={formData?.expiryDate}
              onChange={(e) => handleInputChange('expiryDate', e?.target?.value)}
              error={errors?.expiryDate}
              required
            />

            <Input
              label="Batch Size"
              type="number"
              placeholder="e.g., 10000"
              value={formData?.batchSize}
              onChange={(e) => handleInputChange('batchSize', e?.target?.value)}
              error={errors?.batchSize}
              required
              description="Units produced in this batch"
            />
          </div>
        </div>

        {/* Production Specifications */}
        <div className="border-t border-border pt-6">
          <h4 className="text-md font-medium text-foreground mb-4">Production Specifications</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Manufacturing Site"
              placeholder="Select manufacturing site"
              options={manufacturingSiteOptions}
              value={formData?.manufacturingSite}
              onChange={(value) => handleInputChange('manufacturingSite', value)}
              error={errors?.manufacturingSite}
              required
            />

            <Select
              label="Storage Conditions"
              placeholder="Select storage conditions"
              options={storageConditionOptions}
              value={formData?.storageConditions}
              onChange={(value) => handleInputChange('storageConditions', value)}
              error={errors?.storageConditions}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Input
              label="Packaging Type"
              type="text"
              placeholder="e.g., Blister pack, Bottle"
              value={formData?.packagingType}
              onChange={(e) => handleInputChange('packagingType', e?.target?.value)}
              error={errors?.packagingType}
            />

            <Select
              label="Quality Grade"
              placeholder="Select quality grade"
              options={qualityGradeOptions}
              value={formData?.qualityGrade}
              onChange={(value) => handleInputChange('qualityGrade', value)}
              error={errors?.qualityGrade}
            />
          </div>
        </div>

        {/* Additional Notes */}
        <div className="border-t border-border pt-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Notes
              </label>
              <textarea
                rows={4}
                placeholder="Enter any additional batch information, special handling requirements, or observations..."
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={isSubmitting}
          >
            Reset Form
          </Button>

          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="secondary"
              disabled={isSubmitting}
            >
              Save as Draft
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              iconName="Save"
              iconPosition="left"
            >
              {isEditing ? 'Update Batch' : 'Create Batch'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BatchInfoForm;